/**
 * Web Bluetooth transport for BLE ELM327 adapters.
 *
 * These clones wrap a serial port in a GATT service, and there is no standard
 * for which one. Four layouts cover essentially the whole market, so rather
 * than hard-coding a UUID we connect, walk the services, and pick the first
 * that offers a notify characteristic and a writable one.
 *
 * Browser support: Chrome and Edge on Android, Windows, macOS and Linux.
 * Safari and Firefox do not implement Web Bluetooth at all, and iOS cannot
 * (every iOS browser is WebKit underneath).
 */
import type { Transport } from '@obd/core';

/** Services worth asking permission for, most common first. */
const CANDIDATE_SERVICES: BluetoothServiceUUID[] = [
  0xfff0, // Vgate / Viecar / most "Bluetooth 4.0 ELM327 v1.5" clones
  0xffe0, // HM-10 style modules
  0x18f0, // LELink
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART
  0xfd00,
  0xffb0,
];

/**
 * Characteristics to probe by name when a stack refuses to enumerate them.
 * These are the notify/write pairs used by the services listed above.
 */
const CANDIDATE_CHARACTERISTICS: BluetoothCharacteristicUUID[] = [
  0xfff1, 0xfff2, 0xffe1, 0x2af0, 0x2af1, 0xffb1, 0xffb2,
  '6e400002-b5a3-f393-e0a9-e50e24dcca9e',
  '6e400003-b5a3-f393-e0a9-e50e24dcca9e',
];

/** Name fragments these adapters advertise under. */
const NAME_PREFIXES = ['OBD', 'obd', 'ELM', 'Vgate', 'VEEPEAK', 'Viecar', 'IOS-Vlink', 'V-LINK', 'KONNWEI', 'CBT'];

export interface BleConnectOptions {
  /** Show every nearby device rather than filtering by name. */
  acceptAll?: boolean;
  /**
   * Connect straight to this device, skipping the chooser. Used for the
   * adapter the browser already remembers, so opening the app is enough.
   */
  device?: BluetoothDevice;
  onLog?: (msg: string) => void;
}

/**
 * Whether the browser can hand back adapters this app was already granted.
 *
 * `getDevices()` is what makes connecting without a tap possible after the app
 * is reopened. Chrome has it behind a flag on some platforms, so it is feature
 * detected and the UI says what is actually available.
 */
export function canRememberAdapters(): boolean {
  return (
    isWebBluetoothAvailable() &&
    typeof (navigator.bluetooth as { getDevices?: unknown }).getDevices === 'function'
  );
}

/**
 * Adapters this app already has permission for, most likely first.
 *
 * Returns an empty list rather than throwing: no permission, no Bluetooth, or
 * a browser without the API all mean the same thing to the caller.
 */
export async function rememberedAdapters(): Promise<BluetoothDevice[]> {
  try {
    if (!canRememberAdapters()) return [];
    const devices = await (navigator.bluetooth as { getDevices: () => Promise<BluetoothDevice[]> }).getDevices();
    const named = devices.filter((d) => NAME_PREFIXES.some((p) => (d.name ?? '').includes(p)));
    // A named adapter is the likely one; anything else granted comes after.
    return [...named, ...devices.filter((d) => !named.includes(d))];
  } catch {
    return [];
  }
}

export function isWebBluetoothAvailable(): boolean {
  // Read through an index signature: @types/web-bluetooth declares `bluetooth`
  // as always present, which would narrow the negative branch away entirely.
  return typeof navigator !== 'undefined' && (navigator as { bluetooth?: unknown }).bluetooth !== undefined;
}

/** What the user can actually do about an unavailable Bluetooth stack. */
export type BluetoothRemedy = 'ios' | 'insecure-context' | 'other-browser' | 'none';

export interface BluetoothAvailability {
  available: boolean;
  /** One sentence on why not. Absent when available. */
  reason?: string;
  remedy: BluetoothRemedy;
  /** True on an Apple mobile device, where the remedies are iOS-specific. */
  isApple: boolean;
}

/**
 * Work out whether we can talk Bluetooth, and if not, what would fix it.
 *
 * Availability is decided by feature detection, never by sniffing the user
 * agent. That matters on iOS specifically: the third-party browsers and the
 * Safari extension that bring Web Bluetooth to iPhones all work by injecting
 * `navigator.bluetooth`, so a feature check picks them up automatically and
 * the app just works. The user agent is consulted only to word the advice.
 */
export function bluetoothAvailability(): BluetoothAvailability {
  if (typeof navigator === 'undefined') {
    return { available: false, reason: 'No browser environment.', remedy: 'none', isApple: false };
  }

  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac, so the touch-point count disambiguates.
  const isApple =
    /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);

  if (!isWebBluetoothAvailable()) {
    if (isApple) {
      return {
        available: false,
        reason:
          'Safari does not implement Web Bluetooth, and every iOS browser is Safari underneath. ' +
          'It can be added, though — see the options below.',
        remedy: 'ios',
        isApple: true,
      };
    }
    if (/Firefox/.test(ua)) {
      return { available: false, reason: 'Firefox does not implement Web Bluetooth.', remedy: 'other-browser', isApple: false };
    }
    if (/Safari/.test(ua) && !/Chrome|Chromium/.test(ua)) {
      return { available: false, reason: 'Safari does not implement Web Bluetooth.', remedy: 'other-browser', isApple: false };
    }
    return {
      available: false,
      reason: 'This browser does not implement Web Bluetooth.',
      remedy: 'other-browser',
      isApple: false,
    };
  }

  if (!window.isSecureContext) {
    return {
      available: false,
      reason: 'Web Bluetooth needs a secure context, and this page is not one.',
      remedy: 'insecure-context',
      isApple,
    };
  }

  return { available: true, remedy: 'none', isApple };
}

/** Convenience wrapper for callers that only want the sentence. */
export function webBluetoothUnavailableReason(): string | null {
  const a = bluetoothAvailability();
  return a.available ? null : (a.reason ?? 'Web Bluetooth is unavailable.');
}

export class WebBluetoothTransport implements Transport {
  readonly kind = 'ble';
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private writeChar: BluetoothRemoteGATTCharacteristic | null = null;
  private notifyChar: BluetoothRemoteGATTCharacteristic | null = null;
  private dataCbs = new Set<(c: Uint8Array) => void>();
  private closeCbs = new Set<(r?: string) => void>();
  private _label = 'Bluetooth adapter';
  private writeChain: Promise<void> = Promise.resolve();
  /** Most BLE 4.0 links negotiate a 23-byte ATT MTU, leaving 20 for payload. */
  private chunkSize = 20;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  /** Guards against reporting the same drop from both event and watchdog. */
  private closeReported = false;
  /** Whether the disconnect listener is already on the device. */
  private watching = false;

  constructor(private readonly opts: BleConnectOptions = {}) {
    if (opts.device) {
      this.device = opts.device;
      this._label = opts.device.name ?? 'Bluetooth adapter';
    }
  }

  get label(): string {
    return this._label;
  }

  get connected(): boolean {
    return this.server?.connected === true && this.writeChar !== null;
  }

  private log(msg: string): void {
    this.opts.onLog?.(msg);
  }

  /**
   * Open the link.
   *
   * If a device has already been chosen this reopens it directly. The browser
   * keeps the permission for the life of the page, so recovering a dropped
   * link mid-drive never puts a chooser in front of the driver -- only the
   * very first connection needs a tap.
   */
  async connect(): Promise<void> {
    const reason = webBluetoothUnavailableReason();
    if (reason) throw new Error(reason);

    if (this.device) {
      this.log('Reopening ' + this._label);
      // A device handed to us by the browser needs its disconnect watcher too.
      if (!this.watching) {
        this.watching = true;
        this.device.addEventListener('gattserverdisconnected', () => this.reportClosed());
      }
      await this.openGatt();
      return;
    }

    // The chooser must be opened from a user gesture; the caller guarantees it.
    const filters: BluetoothLEScanFilter[] = NAME_PREFIXES.map((namePrefix) => ({ namePrefix }));
    const request: RequestDeviceOptions = this.opts.acceptAll
      ? { acceptAllDevices: true, optionalServices: CANDIDATE_SERVICES }
      : { filters, optionalServices: CANDIDATE_SERVICES };

    this.device = await navigator.bluetooth.requestDevice(request);
    this._label = this.device.name ?? 'Bluetooth adapter';
    this.log('Selected ' + this._label);

    this.watching = true;
    this.device.addEventListener('gattserverdisconnected', () => this.reportClosed());

    await this.openGatt();
  }

  /** True once a device has been picked, so reconnecting is worth attempting. */
  get hasDevice(): boolean {
    return this.device !== null;
  }

  private async openGatt(): Promise<void> {
    const server = await this.device?.gatt?.connect();
    if (!server) throw new Error('Could not open a GATT connection to ' + this._label);
    this.server = server;
    this.log('GATT connected, looking for a serial service');

    await this.bindCharacteristics(server);

    if (!this.writeChar || !this.notifyChar) {
      throw new Error(
        'Connected to ' + this._label + ' but found no usable serial service. ' +
          'This device may not be an ELM327-compatible adapter.',
      );
    }

    await this.notifyChar.startNotifications();
    this.notifyChar.addEventListener('characteristicvaluechanged', (ev: Event) => {
      const target = ev.target as BluetoothRemoteGATTCharacteristic;
      const v = target.value;
      if (!v) return;
      const bytes = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
      // Copy: the DataView is reused by the browser between notifications.
      const copy = new Uint8Array(bytes);
      for (const cb of this.dataCbs) cb(copy);
    });

    this.closeReported = false;
    this.startWatchdog();
    this.log('Serial link ready on ' + this.notifyChar.uuid);
  }

  /**
   * Poll the GATT server's connected flag.
   *
   * `gattserverdisconnected` is the proper signal, but the Web Bluetooth
   * polyfills used on iOS do not reliably fire it. Without a fallback a dropped
   * link would simply go quiet -- the poll loop would keep timing out and
   * auto-reconnect would never be triggered.
   */
  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdog = setInterval(() => {
      if (this.server && this.server.connected === false) this.reportClosed();
    }, 2000);
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  private reportClosed(): void {
    if (this.closeReported) return;
    this.closeReported = true;
    this.stopWatchdog();
    this.writeChar = null;
    this.notifyChar = null;
    for (const cb of this.closeCbs) cb('Adapter disconnected');
  }

  /**
   * Find a notify/write characteristic pair.
   *
   * Some adapters expose both roles on a single characteristic, some split them
   * across two, and the UUIDs differ per vendor, so this is capability-based
   * rather than a lookup table.
   */
  private async bindCharacteristics(server: BluetoothRemoteGATTServer): Promise<void> {
    let services: BluetoothRemoteGATTService[] = [];
    try {
      services = await server.getPrimaryServices();
    } catch {
      // Some platforms refuse a bare enumeration; fall back to the known list.
      for (const uuid of CANDIDATE_SERVICES) {
        try {
          services.push(await server.getPrimaryService(uuid));
        } catch {
          /* this adapter does not have that one */
        }
      }
    }

    for (const service of services) {
      let chars: BluetoothRemoteGATTCharacteristic[] = [];
      try {
        chars = await service.getCharacteristics();
      } catch {
        // Same story as service enumeration: some stacks only answer for a
        // characteristic named explicitly, so try the usual serial UUIDs.
        for (const uuid of CANDIDATE_CHARACTERISTICS) {
          try {
            chars.push(await service.getCharacteristic(uuid));
          } catch {
            /* not on this service */
          }
        }
      }
      if (chars.length === 0) continue;

      const notify = chars.find((c) => c.properties.notify || c.properties.indicate);
      const write = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse);
      if (notify && write) {
        this.notifyChar = notify;
        this.writeChar = write;
        this.log('Using service ' + service.uuid);
        return;
      }
    }
  }

  async disconnect(): Promise<void> {
    this.stopWatchdog();
    this.closeReported = true;
    try {
      if (this.notifyChar) await this.notifyChar.stopNotifications();
    } catch {
      /* already gone */
    }
    this.writeChar = null;
    this.notifyChar = null;
    if (this.server?.connected) this.server.disconnect();
    this.server = null;
    // The device reference is deliberately kept so `connect` can reopen it
    // without a chooser. Use `forget` to drop it entirely.
  }

  /** Drop the chosen device so the next connect shows the picker again. */
  forget(): void {
    this.device = null;
  }

  onData(cb: (chunk: Uint8Array) => void): () => void {
    this.dataCbs.add(cb);
    return () => this.dataCbs.delete(cb);
  }

  onClose(cb: (reason?: string) => void): () => void {
    this.closeCbs.add(cb);
    return () => this.closeCbs.delete(cb);
  }

  /**
   * Writes are serialised and split to the MTU. Overlapping GATT writes throw
   * "operation already in progress" on most stacks, and an ELM327 command must
   * arrive as one uninterrupted line.
   */
  async write(data: Uint8Array): Promise<void> {
    const run = async (): Promise<void> => {
      const char = this.writeChar;
      if (!char) throw new Error('Bluetooth adapter is not connected');
      await writeChunked(char, data, this.chunkSize);
    };
    const next = this.writeChain.then(run, run);
    this.writeChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }
}

/**
 * Minimal shape of a writable characteristic, so this can be tested without a
 * browser and without the full Web Bluetooth type surface.
 */
export interface WritableCharacteristic {
  properties?: { writeWithoutResponse?: boolean; write?: boolean };
  writeValueWithoutResponse?: (v: BufferSource) => Promise<void>;
  writeValueWithResponse?: (v: BufferSource) => Promise<void>;
  writeValue?: (v: BufferSource) => Promise<void>;
}

/**
 * Write a command, split to the MTU, using whichever write method exists.
 *
 * The split matters because an ELM327 command has to arrive as one
 * uninterrupted line and BLE 4.0 links usually negotiate a 23-byte ATT MTU.
 *
 * The method fallback matters because `writeValueWithResponse` and
 * `writeValueWithoutResponse` are the newer names. The Web Bluetooth polyfills
 * that make this work on iOS -- and older Android WebViews -- frequently
 * implement only the deprecated `writeValue`, so assuming the modern pair
 * silently breaks the entire connection on exactly the platforms we most need
 * to support.
 */
export async function writeChunked(
  char: WritableCharacteristic,
  data: Uint8Array,
  chunkSize: number,
): Promise<void> {
  const preferNoResponse = char.properties?.writeWithoutResponse === true;

  for (let off = 0; off < data.length; off += chunkSize) {
    const chunk = data.slice(off, off + chunkSize);
    if (preferNoResponse && char.writeValueWithoutResponse) {
      await char.writeValueWithoutResponse(chunk);
    } else if (char.writeValueWithResponse) {
      await char.writeValueWithResponse(chunk);
    } else if (char.writeValue) {
      await char.writeValue(chunk);
    } else if (char.writeValueWithoutResponse) {
      await char.writeValueWithoutResponse(chunk);
    } else {
      throw new Error('This Bluetooth characteristic exposes no write method.');
    }
  }
}
